import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';
import { deleteCallLog, deleteTextLog, deleteVoiceMailLog } from '@/api/logs';
import { LogEntryWithType } from '@/types/TInbox';
import { MutationStatus } from 'src/enums/ELogs';

export const useDeleteHandler = (
	selectedLog: LogEntryWithType | null,
	setSelectedLog: (log: LogEntryWithType | null) => void,
	refetchCallLogs: () => void,
	refetchTextLogs: () => void,
	refetchVoicemails: () => void,
) => {
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [logToDelete, setLogToDelete] = useState<LogEntryWithType | null>(null);

	const { mutate: deleteCallLogMutation, status: deleteCallLogStatus } = useMutation({
		mutationKey: ['deleteCallLog'],
		mutationFn: deleteCallLog,
		onSuccess() {
			refetchCallLogs();
			setConfirmDelete(false);
			setLogToDelete(null);
			if (selectedLog?.id === logToDelete?.id) {
				setSelectedLog(null);
			}
			notification.success({ message: 'Call log deleted successfully' });
		},
		onError() {
			notification.error({ message: 'Failed to delete call log' });
			setConfirmDelete(false);
			setLogToDelete(null);
		},
	});

	const { mutate: deleteTextLogMutation, status: deleteTextLogStatus } = useMutation({
		mutationKey: ['deleteTextLog'],
		mutationFn: deleteTextLog,
		onSuccess() {
			refetchTextLogs();
			setConfirmDelete(false);
			setLogToDelete(null);
			if (selectedLog?.id === logToDelete?.id) {
				setSelectedLog(null);
			}
			notification.success({ message: 'Text log deleted successfully' });
		},
		onError() {
			notification.error({ message: 'Failed to delete text log' });
			setConfirmDelete(false);
			setLogToDelete(null);
		},
	});

	const { mutate: deleteVoiceMailLogMutation, status: deleteVoiceMailLogStatus } = useMutation({
		mutationKey: ['deleteVoiceMailLog'],
		mutationFn: deleteVoiceMailLog,
		onSuccess() {
			refetchVoicemails();
			setConfirmDelete(false);
			setLogToDelete(null);
			if (selectedLog?.id === logToDelete?.id) {
				setSelectedLog(null);
			}
			notification.success({ message: 'Voicemail log deleted successfully' });
		},
		onError() {
			notification.error({ message: 'Failed to delete voicemail log' });
			setConfirmDelete(false);
			setLogToDelete(null);
		},
	});

	const handleDeleteLog = (log: LogEntryWithType) => {
		setLogToDelete(log);
		setConfirmDelete(true);
	};

	const handleConfirmDelete = (selectedAgentId: string) => {
		if (!logToDelete || !selectedAgentId) {
			return;
		}

		switch (logToDelete.type) {
			case 'call':
				if (logToDelete.callSid) {
					deleteCallLogMutation({
						callSid: logToDelete.callSid,
						date: logToDelete.date,
						agentId: selectedAgentId,
					});
				} else {
					notification.error({ message: 'Missing call information' });
					setConfirmDelete(false);
					setLogToDelete(null);
				}
				break;
			case 'text':
				deleteTextLogMutation({
					textLogDate: logToDelete.date,
					agentId: selectedAgentId,
				});
				break;
			case 'voicemail':
				deleteVoiceMailLogMutation({
					voiceMailLogDate: logToDelete.date,
					agentId: selectedAgentId,
				});
				break;
			default:
				notification.error({ message: 'Unknown log type' });
				setConfirmDelete(false);
				setLogToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setConfirmDelete(false);
		setLogToDelete(null);
	};

	const isDeletingLog =
		deleteCallLogStatus === MutationStatus.PENDING ||
		deleteTextLogStatus === MutationStatus.PENDING ||
		deleteVoiceMailLogStatus === MutationStatus.PENDING;

	return {
		confirmDelete,
		logToDelete,
		isDeletingLog,
		handleDeleteLog,
		handleConfirmDelete,
		handleCancelDelete,
	};
};
